const READ_ARGUMENT_PATTERN = /"([^"]*)"|\[(@?[A-Z0-9_#]+)\]/gi;
const SKIP_LITERAL_PATTERN = /^\|(\d+)\|$/;

function stripTechnicalSuffix(variableName = '') {
  return variableName.replace(/#[A-Z]$/i, '');
}

function cleanReadSource(source = '') {
  return source.replace(/:$/, '');
}

function createReadDiagnostic(code, message) {
  return {
    level: 'warning',
    code,
    message,
  };
}

function normalizeReadInput(raw = '') {
  return raw.trim().replace(/^READ\b\s*/i, '');
}

function parseReadArguments(expression, sourceMatch, rowMatch, columnMatch) {
  const literals = [];
  const variables = [];
  const parseParts = [];

  const anchorMatch = columnMatch || rowMatch || sourceMatch;

  if (!anchorMatch) {
    return {
      literals,
      variables,
      search: null,
      target: null,
      until: null,
      targets: variables,
      parseParts,
    };
  }

  const tail = expression.slice(anchorMatch.index + anchorMatch[0].length);
  let match;
  let foundTarget = false;
  let search = null;
  let until = null;

  READ_ARGUMENT_PATTERN.lastIndex = 0;

  while ((match = READ_ARGUMENT_PATTERN.exec(tail)) !== null) {
    if (match[2]) {
      const variable = stripTechnicalSuffix(match[2]);
      variables.push(variable);
      parseParts.push({
        type: 'variable',
        target: variable,
      });

      if (!foundTarget) {
        foundTarget = true;
      }

      continue;
    }

    const skipMatch = match[1].match(SKIP_LITERAL_PATTERN);

    if (skipMatch) {
      parseParts.push({
        type: 'skip',
        value: Number(skipMatch[1]),
        raw: match[1],
      });
    } else {
      literals.push(match[1]);
      parseParts.push({
        type: !foundTarget && search === null ? 'search' : 'literal',
        value: match[1],
      });
    }

    if (!foundTarget && search === null && !skipMatch) {
      search = match[1];
      continue;
    }

    if (foundTarget && until === null && !skipMatch) {
      until = match[1];
    }
  }

  const firstPart = parseParts[0];
  const implicitRowRef =
    !rowMatch && firstPart?.type === 'variable' && firstPart.target.toUpperCase() === '@DATA_ROW'
      ? firstPart.target
      : null;
  const resolvedVariables = implicitRowRef ? variables.slice(1) : variables;
  const resolvedParseParts = implicitRowRef ? parseParts.slice(1) : parseParts;

  return {
    literals,
    variables: resolvedVariables,
    search,
    target: resolvedVariables[0] || null,
    targets: resolvedVariables,
    parseParts: resolvedParseParts,
    until,
    implicitRowRef,
  };
}

export function parseReadInstruction(raw = '') {
  const expression = normalizeReadInput(raw);
  const sourceMatch = expression.match(/\bF\s*=\s*"([^"]*)"/i);
  const rowMatch = expression.match(/\bR\s*=\s*(\d+|\[(@?[A-Z0-9_#]+)\])/i);
  const columnMatch = expression.match(/\bC\s*=\s*(\d+|\[(@?[A-Z0-9_#]+)\])/i);
  const readArguments = parseReadArguments(expression, sourceMatch, rowMatch, columnMatch);
  const diagnostics = [];
  const rowRef = rowMatch?.[2] || readArguments.implicitRowRef || (!rowMatch && columnMatch ? '@DATA_ROW' : null);
  const hasRowSelector = Boolean(rowMatch || rowRef || readArguments.search !== null);
  const hasReadEffect = Boolean(readArguments.target || readArguments.search !== null);

  if (!sourceMatch || !hasRowSelector || !hasReadEffect) {
    diagnostics.push(createReadDiagnostic('READ_PARSE_UNCERTAIN', 'READ no pudo interpretarse completamente'));
  }

  if (readArguments.variables.length > 1) {
    diagnostics.push(
      {
        ...createReadDiagnostic('READ_MULTIPLE_TARGETS', 'READ contiene varias variables destino y se procesaran en el orden declarado.'),
        level: 'info',
      }
    );
  }

  const parseStatus = diagnostics.some((diagnostic) => diagnostic.code === 'READ_PARSE_UNCERTAIN') ? 'unknown' : 'parsed';

  return {
    type: 'read',
    source: sourceMatch ? cleanReadSource(sourceMatch[1]) : null,
    row: rowMatch && /^\d+$/.test(rowMatch[1]) ? Number(rowMatch[1]) : null,
    rowRef,
    column: columnMatch && /^\d+$/.test(columnMatch[1]) ? Number(columnMatch[1]) : null,
    columnRef: columnMatch?.[2] || null,
    search: readArguments.search,
    target: readArguments.target,
    targets: readArguments.targets,
    variables: readArguments.variables,
    literals: readArguments.literals,
    parseParts: readArguments.parseParts,
    until: readArguments.until,
    parseStatus,
    visible: false,
    raw,
    ...(diagnostics.length > 0
      ? {
          diagnostic: diagnostics[0],
          diagnostics,
        }
      : {}),
  };
}
